<template lang="pug">
.curso-main-container.pb-3
  BannerInterno
  .container.tarjeta.tarjeta--blanca.p-4.p-md-5.mb-5
    .titulo-principal.color-acento-contenido
      .titulo-principal__numero
        span 3
      h1 Comunicación comercial sostenible

    img(data-aos="fade-up")(src="@/assets/curso/temas/tema3/1.png", alt="Imagen decorativa")

    .row.justify-content-center
      .col-lg-10(data-aos="fade-down")
        div.banner-text.bg-white
          p.mb-0 La comunicación comercial sostenible transmite mensajes claros, honestos y alineados con valores ambientales y sociales. Busca conectar con consumidores conscientes sin recurrir al greenwashing ni a la exageración. Es una herramienta clave para construir confianza, educar al cliente y fortalecer la reputación de marca.

    Separador
    #t_3_1(data-aos='zoom-in-right').titulo-segundo.color-acento-contenido
      h3 3.1 Concepto

    .row.justify-content-center.align-items-center
      .col-8.col-md-6.col-lg-3
        img.mb-4.mb-lg-0(src="@/assets/curso/temas/tema3/2.png", alt="Imagen decorativa", data-aos="fade-up")
      .col-lg-9
        p La comunicación comercial sostenible es una estrategia de <em>marketing</em> que busca integrar valores medioambientales, sociales y éticos en todos los mensajes publicitarios o promocionales de una organización. Su propósito no se limita únicamente a aumentar las ventas, sino que también apunta a fomentar un consumo más responsable, educar al consumidor y construir una reputación basada en la responsabilidad social. Este tipo de comunicación responde a las exigencias de un mercado cada vez más consciente, donde los consumidores valoran las acciones coherentes con el respeto al medio ambiente, los derechos humanos y la justicia social.
        .cajon.color-acento-contenido.p-4
          p.mb-0 La comunicación sostenible no solo beneficia al planeta y a la sociedad, sino que también fortalece el vínculo emocional con los clientes, construyendo una marca con propósito y mayor valor percibido. Se fundamenta en la transparencia, coherencia, y compromiso a largo plazo, evitando el "<em>greenwashing</em>" que es el uso engañoso de mensajes ecológicos.

    Separador
    #t_3_2(data-aos='zoom-in-right').titulo-segundo.color-acento-contenido
      h3 3.2 Clases

    p.mb-5 La comunicación comercial sostenible se manifiesta en distintas formas según su enfoque. A continuación, se describen tres tipos principales: ambiental, social y ética, cada uno con objetivos específicos y ejemplos de aplicación.


    .row.justify-content-center.align-items-center
      .col-xl-10
        h4(data-aos='zoom-in-right').mb-5
          span.decorated-title Clases de comunicación comercial
          
        AcordionA(tipo="a" clase-tarjeta="tarjeta tarjeta--primario-p20")
          .row.align-items-center.justify-content-center.ps-5(titulo="Comunicación ambiental")
            .col-lg-8
              p Tiene como objetivo concientizar sobre prácticas que contribuyen a la protección del medio ambiente. Ejemplos:
              ul.lista-ul--color.mb-4.mb-lg-0
                li.d-flex 
                  i.fa-solid.fa-circle-check
                  | Campañas sobre ahorro energético o reducción de emisiones.
                li.d-flex 
                  i.fa-solid.fa-circle-check
                  | Promoción de productos biodegradables o con empaques reciclables.
                li.d-flex 
                  i.fa-solid.fa-circle-check
                  | Acciones para plantar árboles o limpiar espacios naturales, promovidas por la empresa.

            .col-sm-8.col-lg-4.col-xl-3
              figure
                img(src='@/assets/curso/temas/tema3/3.png', alt='Imagen de acompañamiento')
          .row.align-items-center.justify-content-center.ps-5(titulo="Comunicación social")
            .col-lg-8
              p Resalta el compromiso de la empresa con la mejora de la calidad de vida de las personas y comunidades. Ejemplos:    
              ul.lista-ul--color.mb-4.mb-lg-0
                li.d-flex 
                  i.fa-solid.fa-circle-check
                  | Inclusión de comunidades indígenas o rurales en la cadena de valor.
                li.d-flex 
                  i.fa-solid.fa-circle-check
                  | Apoyo a causas como la educación, la salud o la lucha contra la violencia de género.
                li.d-flex 
                  i.fa-solid.fa-circle-check
                  | Proyectos de empleabilidad para personas con discapacidad.

            .col-sm-8.col-lg-4.col-xl-3
              figure
                img(src='@/assets/curso/temas/tema3/4.png', alt='Imagen de acompañamiento')
          .row.align-items-center.justify-content-center.ps-5(titulo="Comunicación ética")
            .col-lg-8
              p Se centra en la integridad y transparencia del mensaje, evitando exageraciones o promesas falsas. Incluye:
              ul.lista-ul--color.mb-4.mb-lg-0
                li.d-flex 
                  i.fa-solid.fa-circle-check
                  | Información clara sobre el origen de los productos.
                li.d-flex 
                  i.fa-solid.fa-circle-check
                  | Respeto por los derechos laborales y condiciones de trabajo justas.
                li.d-flex 
                  i.fa-solid.fa-circle-check
                  | Evitación de estereotipos o discriminación en la publicidad.

            .col-sm-8.col-lg-4.col-xl-3
              figure
                img(src='@/assets/curso/temas/tema3/5.png', alt='Imagen de acompañamiento')

    Separador
    #t_3_3(data-aos='zoom-in-right').titulo-segundo.color-acento-contenido
      h3 3.3 Usos

    p.mb-5 La comunicación comercial sostenible puede integrarse en distintas estrategias prácticas que refuerzan el compromiso ambiental y social de las empresas, generando valor tanto para la marca como para la sociedad. Algunas de las más utilizadas son:

    .row.align-items-stretch.justify-content-center
      .col-lg-6.mb-4.height-available
        .p-4.bg-waves.ps-xl-5.h-100
          p.mb-0.ps-xl-5 Campañas con propósito: lanzamientos de productos que destinan parte de sus ganancias a causas sociales o ambientales. Ejemplo: una marca de ropa que dona un porcentaje de sus ventas para reforestar zonas afectadas.
      .col-lg-6.mb-4.height-available
        .p-4.bg-waves.ps-xl-5.h-100
          p.mb-0.ps-xl-5 Informes de sostenibilidad o memorias RSE: publicaciones anuales que muestran el desempeño de la empresa en términos sociales, ambientales y económicos. Ejemplo: un informe que presenta cuánto plástico reciclado utilizó la empresa en sus productos.
      .col-lg-6.mb-4.mb-lg-0.height-available
        .p-4.bg-waves.bg-waves--purple.ps-xl-5.h-100
          p.mb-0.ps-xl-5 Etiquetado ecológico: certificaciones que ayudan a los consumidores a identificar productos respetuosos con el medio ambiente. Ejemplo: sello FSC para productos de madera y papel sostenibles, o el sello de comercio justo.
      .col-lg-6.mb-4.mb-lg-0.height-available
        .p-4.bg-waves.bg-waves--purple.ps-xl-5.h-100
          p.mb-0.ps-xl-5 Educación al consumidor: a través de <em>blogs</em>, videos, talleres o redes sociales, se promueven hábitos de consumo responsable. Ejemplo: tutoriales sobre cómo reutilizar envases o compostar residuos.

    Separador
    #t_3_4(data-aos='zoom-in-right').titulo-segundo.color-acento-contenido
      h3 3.4 Tipos de material promocional

    p.mb-5 Dentro de la comunicación comercial sostenible, el material promocional juega un papel clave al transmitir el compromiso de la empresa con el medio ambiente. A continuación, se presentan algunos ejemplos de materiales utilizados con enfoque ecológico:

    h4(data-aos='zoom-in-right').mb-5
      span.decorated-title Tipos de material promocional sostenible

    .row.justify-content-center.align-items-center.mb-5
      .col-lg-4.d-none.d-xl-block
        figure
          img(src='@/assets/curso/temas/tema3/6.png', alt='Imagen de acompañamiento', data-aos="fade-up")
      .col-xl-8.p-5.bg-slider.rounded-3
        .tarjeta.tarjeta--blanca.p-4.py-5.mb-2
          SlyderA(tipo="b")
            .row.align-items-center.justify-content-center
              .col-sm-10.col-lg-8.col-xl-9
                h5.mb-4 Folletos reciclables
                p.mb-4.mb-lg-0 Impresos con papel reciclado o con certificación FSC, tinta vegetal o sin tóxicos.
              .col-sm-8.col-lg-3.col-xl-2
                figure
                  img.mb-4.mb-lg-0.w-81(src='@/assets/curso/temas/tema3/7.svg', alt='Imagen ilustrativa')
                  
            .row.align-items-center.justify-content-center
              .col-sm-10.col-lg-8.col-xl-9
                h5.mb-4 Productos reutilizables
                p.mb-4.mb-lg-0 Termos, bolsas ecológicas, camisetas de algodón orgánico con el logo de la marca.
              .col-sm-8.col-lg-3.col-xl-2
                figure
                  img.mb-4.mb-lg-0.w-81(src='@/assets/curso/temas/tema3/8.svg', alt='Imagen ilustrativa')

            .row.align-items-center.justify-content-center
              .col-sm-10.col-lg-8.col-xl-9
                h5.mb-4 Digitales
                p.mb-4.mb-lg-0 Videos educativos, publicaciones en redes sociales, <em>newsletters</em> o <em>podcasts</em> que promueven buenas prácticas.
              .col-sm-8.col-lg-3.col-xl-2
                figure
                  img.mb-4.mb-lg-0.w-81(src='@/assets/curso/temas/tema3/9.svg', alt='Imagen ilustrativa')

            .row.align-items-center.justify-content-center
              .col-sm-10.col-lg-8.col-xl-9
                h5.mb-4 <em>Packaging</em> sostenible
                p.mb-4.mb-lg-0 Envases compostables, biodegradables o reutilizables con mensajes de sostenibilidad.
              .col-sm-8.col-lg-3.col-xl-2
                figure
                  img.mb-4.mb-lg-0.w-81(src='@/assets/curso/temas/tema3/10.svg', alt='Imagen ilustrativa')

            .row.align-items-center.justify-content-center
              .col-sm-10.col-lg-8.col-xl-9
                h5.mb-4 <em>Merchandising</em> ecológico
                p.mb-4.mb-lg-0 Semilleros, lápices plantables, libretas hechas con papel de caña o fibras naturales.
              .col-sm-8.col-lg-3.col-xl-2
                figure
                  img.mb-4.mb-lg-0.w-81(src='@/assets/curso/temas/tema3/11.svg', alt='Imagen ilustrativa')

    p.mb-5 Se recomienda revisar el siguiente video: plan de comunicación de producto sostenible  

    figure.mb-5
      .video.mb-2
        iframe(width="560" height="315" src="https://www.youtube.com/embed/H891NO0WKdk" title="Plan de comunicación de producto sostenible" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen)
      figcaption Video: plan de comunicación de producto sostenible

</template>

<script>
export default {
  name: 'Tema3',
  data: () => ({
    // variables de vue
  }),
  mounted() {
    this.$nextTick(() => {
      this.$aosRefresh()
    })
  },
  updated() {
    this.$aosRefresh()
  },
}
</script>

<style lang="sass"></style>
